import { Box, Divider, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/providers";
import {
  NavigationPage,
  PageHeader,
  RobotoTypography,
} from "@/shared/components";
import {
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components/RobotoStyledTabs";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { SystemUserViewToggle } from "@/shared/components/SystemUserViewToggle.tsx";
import { OrganizationTier } from "@/shared/domain/orgs/OrgRecord";

import AdminOverview from "./AdminOverview";
import { AISettings } from "./AISettings";
import { Integrations } from "./Integrations";
import { OrganizationSettings } from "./OrganizationSettings";
import { Secrets } from "./Secrets";
import { Tokens } from "./Tokens";
import { Usage } from "./Usage";

export function Settings() {
  const theme = useTheme();

  const { currentOrganization, currentUser } = useAuth();
  const isPremiumOrg = currentOrganization?.tier === OrganizationTier.Premium;

  const [searchParams, setSearchParams] = useSearchParams();

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  return (
    <NavigationPage title={"Roboto - Settings"}>
      <PageHeader>Settings</PageHeader>

      <Box
        sx={{
          paddingTop: theme.spacing(3),
        }}
      >
        <RobotoStyledTabs
          value={tabValue}
          onChange={(_event, newValue) => {
            setSearchParams({ tab: String(newValue) });
          }}
          aria-label="settings tabs"
        >
          <RobotoStyledTab label="Overview" />
          <RobotoStyledTab data-cy="organization" label="Organization" />
          <RobotoStyledTab label="Tokens" />
          <RobotoStyledTab label="Usage" />
          <RobotoStyledTab label="AI" />
          <RobotoStyledTab label="Secrets" />
          {isPremiumOrg && <RobotoStyledTab label="Integrations" />}
        </RobotoStyledTabs>

        <Divider
          sx={{
            borderColor: theme.border.color,
          }}
        />
      </Box>

      <Box
        sx={{
          padding: theme.spacing(2, 0),
          paddingBottom: theme.spacing(1),
        }}
      >
        <RobotoTabPanel
          index={0}
          value={tabValue}
          sx={{
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
          }}
        >
          <RobotoTypography variant="body2">
            Welcome to the Roboto settings panel!
            <br />
            <br />
            Use the tabs above to review your organization, tokens and usage.
          </RobotoTypography>

          <SystemUserOnly enforceOverride={false}>
            <Divider sx={{ margin: theme.spacing(2, 0) }} />
            <SystemUserViewToggle />
          </SystemUserOnly>

          <SystemUserOnly>
            <Divider sx={{ margin: theme.spacing(2, 0) }} />
            <AdminOverview />
          </SystemUserOnly>
        </RobotoTabPanel>

        <RobotoTabPanel
          index={1}
          value={tabValue}
          sx={{
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
          }}
        >
          <OrganizationSettings
            currentOrg={currentOrganization}
            currentUser={currentUser}
          />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={2}
          value={tabValue}
          sx={{
            paddingBottom: theme.spacing(1),
          }}
        >
          <Tokens />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={3}
          value={tabValue}
          sx={{
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(3),
          }}
        >
          <Usage showGraphs={true} />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={4}
          value={tabValue}
          sx={{
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
          }}
        >
          <AISettings />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={5}
          value={tabValue}
          sx={{
            display: "flex",
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
          }}
          innerBoxSx={{
            width: "100%",
          }}
        >
          <Secrets />
        </RobotoTabPanel>

        {isPremiumOrg && (
          <RobotoTabPanel
            index={6}
            value={tabValue}
            sx={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            }}
          >
            <Integrations />
          </RobotoTabPanel>
        )}
      </Box>
    </NavigationPage>
  );
}
